<template>
	<div class="chat-top-message">
		<div class="message-main" @click.stop="onClickMessage">
			<div class="message-icon">
				<i class="el-icon-paperclip"></i>
			</div>
			<div class="message-body">
				<span class="pin-label">置顶</span>
				<span class="sender">{{ showName }}</span>
				<div class="content" v-html="content"></div>
			</div>
		</div>
		<div v-if="hasActions" class="message-actions">
			<button v-if="topCount > 1" type="button" class="count" title="查看全部置顶" @click.stop="onViewAll">
				+{{ topCount - 1 }}
			</button>
			<button
				v-if="canClearAllTopMessages"
				type="button"
				class="close"
				title="取消全部置顶"
				@click.stop="onClose"
			>
				<i class="el-icon-close"></i>
			</button>
		</div>
	</div>
</template>
<script>
export default {
	name: "chatTopMessage",
	props: {
		group: {
			type: Object
		},
		groupMembers: {
			type: Array
		},
		msgInfo: {
			type: Object,
			required: true
		},
		headImage: {
			type: String,
			default: ''
		},
		showName: {
			type: String,
			required: true
		},
		topCount: {
			type: Number,
			default: 0
		}
	},
	methods: {
		onClose() {
			if (!this.canClearAllTopMessages) {
				return;
			}
			this.clearAllTopMessages();
		},
		onClickMessage() {
			this.$emit("locate", this.msgInfo);
		},
		onViewAll() {
			this.$emit("view-all");
		},
		clearAllTopMessages() {
			const { targets, removedIds } = this.clearTargetState;
			if (!targets.length) {
				return;
			}
			const confirmText = `将取消全部${targets.length}条置顶消息，确认继续？`;
			this.$confirm(confirmText, '取消置顶', {
				confirmButtonText: '移除',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(async () => {
				await this.$http({
					url: `/group/removeAllTopMessages/${this.group.id}`,
					method: 'delete'
				});
			}).then(() => {
				this.$message.success('已取消全部置顶');
				this.$emit('sync-top-state', {
					action: 'remove-all',
					msgInfo: this.msgInfo,
					removedIds
				});
			}).catch((error) => {
				if (error !== 'cancel' && error !== 'close') {
					this.$message.error('取消全部置顶失败，请重试');
				}
			});
			},
	},
	computed: {
		content() {
			if (this.msgInfo.type == this.$enums.MESSAGE_TYPE.TEXT) {
				return this.$emo.transform(this.$str.html2Escape(this.msgInfo.content), 'emoji-normal');
			}
			return this.$msgUtil.previewContent(this.msgInfo)
		},
		isOwner() {
			return this.group.ownerId == this.userStore.userInfo.id;
		},
		isManager() {
			if (!this.group || !this.group.id) {
				return false;
			}
			return this.groupStore.isManager(this.group.id, this.userStore.userInfo.id);
		},
		canClearAllTopMessages() {
			return this.isOwner || this.isManager;
		},
		hasActions() {
			return this.topCount > 1 || this.canClearAllTopMessages;
		},
		clearTargetState() {
			const topMessages = Array.isArray(this.group && this.group.topMessages) ? this.group.topMessages : [];
			const targets = topMessages.length ? topMessages : [this.msgInfo];
			const seen = new Set();
			const uniqueTargets = targets.filter((item) => {
				if (!item || !item.id || seen.has(item.id)) {
					return false;
				}
				seen.add(item.id);
				return true;
			}).slice().reverse();
			return {
				targets: uniqueTargets,
				removedIds: uniqueTargets.map((item) => item.id)
			};
		}
	}
}
</script>

<style scoped lang="scss">
.chat-top-message {
	display: flex;
	align-items: center;
	min-height: 38px;
	gap: 10px;
	padding: 0 var(--chat-header-inline-padding) 0;
	background: rgba(var(--im-primary-rgb), 0.03);
	color: var(--im-text-color-light);

	.message-main {
		display: flex;
		align-items: center;
		gap: 10px;
		min-width: 0;
		flex: 1;
		cursor: pointer;
	}

	.message-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		flex-shrink: 0;
		color: var(--im-color-warning);
		border-radius: 6px;
		background: rgba(var(--im-orange-rgb), 0.12);

		i {
			font-size: 12px;
		}
	}

	.message-body {
		min-width: 0;
		flex: 1;
		display: flex;
		align-items: center;
		gap: 10px;
		text-align: left;
	}

	.pin-label {
		flex-shrink: 0;
		display: inline-flex;
		align-items: center;
		height: 20px;
		padding: 0 8px;
		border-radius: 999px;
		background: rgba(var(--im-orange-rgb), 0.12);
		font-size: 11px;
		font-weight: 600;
		color: var(--im-color-warning);
		white-space: nowrap;
	}

	.sender {
		font-size: 12px;
		font-weight: 500;
		color: var(--im-text-color-secondary);
		white-space: nowrap;
		flex-shrink: 0;
		max-width: 128px;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.content {
		flex: 1;
		font-size: 13px;
		color: var(--im-text-color);
		line-height: 1.4;
		word-break: break-all;
		overflow: hidden;
		display: -webkit-box;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
	}

	.count,
	.close {
		flex-shrink: 0;
	}

	.message-actions {
		margin-left: auto;
		display: inline-flex;
		align-items: center;
		justify-content: flex-end;
		gap: 7px;
		flex-shrink: 0;
	}

	.count {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 26px;
		height: 24px;
		padding: 0 7px;
		border: 0;
		border-radius: 999px;
		background: rgba(var(--im-primary-rgb), 0.08);
		color: var(--im-color-primary);
		cursor: pointer;
		font-size: 11px;
		font-weight: 600;
		white-space: nowrap;
	}

	.close {
		width: 24px;
		height: 24px;
		border: 1px solid transparent;
		border-radius: 7px;
		background: transparent;
		cursor: pointer;
		color: var(--im-text-color-light);
		transition: all 0.2s ease;

		&:hover {
			background: rgba(var(--im-black-rgb), 0.05);
			border-color: rgba(var(--im-black-rgb), 0.06);
			color: var(--im-text-color);
		}
	}
}

@media (max-width: 780px) {
	.chat-top-message {
		gap: 6px;

		.message-body {
			gap: 6px;
		}

		.message-actions {
			gap: 6px;
		}

		.pin-label {
			display: none;
		}
	}
}
</style>
